import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { documentsApi } from '@/services/api';
import type { DocumentInstance, DocumentInstanceCreate, DocumentInstancesState, DocumentSummaryCreate } from '@/types';

const initialState: DocumentInstancesState = {
  items: [],
  currentItem: null,
  currentSummary: null,
  loading: false,
  error: null,
  total: 0,
  page: 1,
  size: 10,
};

export const createDocumentInstance = createAsyncThunk(
  'documentInstances/createDocumentInstance',
  async (documentData: DocumentInstanceCreate) => {
    const response = await documentsApi.createDocument(documentData);
    return response;
  }
);

export const fetchDocumentInstances = createAsyncThunk(
  'documentInstances/fetchDocumentInstances',
  async ({ page = 1, size = 10, search }: { page?: number, size?: number, search?: string } = {}) => {
    const response = await documentsApi.getDocuments(page, size, search);
    return response;
  }
);

export const fetchDocumentInstance = createAsyncThunk(
  'documentInstances/fetchDocumentInstance',
  async (id: number) => {
    const response = await documentsApi.getDocument(id);
    return response;
  }
);

export const updateDocumentInstance = createAsyncThunk(
  'documentInstances/updateDocumentInstance',
  async ({ id, documentData }: { id: number, documentData: Partial<DocumentInstanceCreate> }) => {
    const response = await documentsApi.updateDocument(id, documentData);
    return response;
  }
);

export const deleteDocumentInstance = createAsyncThunk(
  'documentInstances/deleteDocumentInstance',
  async (id: number) => {
    await documentsApi.deleteDocument(id);
    return id;
  }
);

export const createDocumentSummary = createAsyncThunk(
  'documentInstances/createDocumentSummary',
  async (summaryData: DocumentSummaryCreate) => {
    const response = await documentsApi.createSummary(summaryData); 
    return response;
  }
);

const documentInstancesSlice = createSlice({
  name: 'documentInstances',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },clearCurrentDocument: (state) => {
    state.currentItem = null;
    state.currentSummary = null;
  }
  },
  extraReducers: (builder) => {
    builder
      // Create document instance
      .addCase(createDocumentInstance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDocumentInstance.fulfilled, (state, action: PayloadAction<DocumentInstance>) => {
        state.loading = false;
        state.items.unshift(action.payload); // Add to beginning of list
        state.total += 1;
      })
      .addCase(createDocumentInstance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create document';
      })
      // Fetch document instances
      .addCase(fetchDocumentInstances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentInstances.fulfilled, (state, action: PayloadAction<{documents: DocumentInstance[], total: number, page: number, size: number}>) => {
        state.loading = false;
        state.items = action.payload.documents;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.size = action.payload.size;
      })
      .addCase(fetchDocumentInstances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch documents';
      })
      // Fetch single document instance
      .addCase(fetchDocumentInstance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentInstance.fulfilled, (state, action: PayloadAction<DocumentInstance>) => {
        state.loading = false;
        state.currentItem = action.payload;
        const document = action.payload;
        // Check if summaries exist and pick the last one
        state.currentSummary = document.summaries && document.summaries.length > 0 
            ? document.summaries[document.summaries.length - 1] 
            : null;
        // Update or add the document to the list
        const existingIndex = state.items.findIndex(item => item.id === action.payload.id);
        if (existingIndex !== -1) {
          state.items[existingIndex] = action.payload;
        } else {
          state.items.unshift(action.payload);
        }
      })
      .addCase(fetchDocumentInstance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch document';
      })
      // Update document instance
      .addCase(updateDocumentInstance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocumentInstance.fulfilled, (state, action: PayloadAction<DocumentInstance>) => {
        state.loading = false;
        // Update the document in the list
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateDocumentInstance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update document';
      })
      // Delete document instance
      .addCase(deleteDocumentInstance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocumentInstance.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        // Remove the document from the list
        state.items = state.items.filter(item => item.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteDocumentInstance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete document';
      }).addCase(createDocumentSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDocumentSummary.fulfilled, (state, action) => {
        state.loading = false;
        
        // 1. Identify which document was updated using the request argument (meta.arg)
        const docId = action.meta.arg.document_instance_id;
        

        if (state.currentItem && state.currentItem.id === docId) {
          state.currentSummary = action.payload;
        }
      })
      .addCase(createDocumentSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create summary';
      });;
  },
});

export const { clearError, clearCurrentDocument } = documentInstancesSlice.actions;
export default documentInstancesSlice.reducer;